import { FormEvent, useEffect, useState, type ReactNode } from "react";
import {
  Link,
  Navigate,
  Outlet,
  Route,
  Routes,
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import type { DashboardSummary, ShareLink } from "@gift-registry/shared";
import {
  ApiError,
  addWishlistMember,
  cloneWishlist,
  createWishlist,
  createWishlistShareLink,
  deleteWishlist,
  listWishlistShareLinks,
  loadDashboard,
  revokeWishlistShareLink,
  resolveApiUrl,
  setWishlistPasscode,
  signIn,
  signUp,
  updateWishlist,
} from "./lib/api.js";
import {
  normalizeSignInValues,
  normalizeSignUpValues,
  resolvePostAuthDestination,
  validateSignInForm,
  validateSignUpForm,
  type SignInErrors,
  type SignInValues,
  type SignUpErrors,
  type SignUpValues,
} from "./lib/forms.js";
import { AuthProvider, useAuth } from "./lib/auth.js";
import { ItemManager } from "./components/item-manager.js";

export default function App() {
  return (
    <AuthProvider>
      <Routes>
        <Route element={<ShellLayout />}>
          <Route index element={<HomePage />} />
          <Route path="signup" element={<SignUpPage />} />
          <Route path="login" element={<SignInPage />} />
          <Route
            path="dashboard"
            element={
              <RequireAuth>
                <DashboardPage />
              </RequireAuth>
            }
          />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
      </Routes>
    </AuthProvider>
  );
}

function ShellLayout() {
  const { status, user, signOut } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  return (
    <div className="page-shell">
      <div className="orb orb-a" aria-hidden="true" />
      <div className="orb orb-b" aria-hidden="true" />
      <header className="topbar">
        <Link to="/" className="brand">
          <span className="brand-mark">GR</span>
          <span className="brand-copy">
            <strong>Gift Registry</strong>
            <small>Cloudflare-connected preview</small>
          </span>
        </Link>

        {status === "authenticated" ? (
          <nav className="nav-actions" aria-label="Primary">
            <Link
              to="/dashboard"
              className={location.pathname === "/dashboard" ? "nav-link active" : "nav-link"}
            >
              Dashboard
            </Link>
            <details className="user-menu">
              <summary>
                <span className="user-avatar">{initials(user?.firstName, user?.lastName)}</span>
                <span>
                  <strong>{user?.firstName}</strong>
                  <small>{user?.email}</small>
                </span>
              </summary>
              <div className="menu-panel">
                <button
                  type="button"
                  className="ghost-button"
                  onClick={async () => {
                    await signOut();
                    navigate("/");
                  }}
                >
                  Sign out
                </button>
              </div>
            </details>
          </nav>
        ) : (
          <div className="nav-actions">
            <Link to="/login" className="nav-link">
              Sign in
            </Link>
            <Link to="/signup" className="primary-button">
              Create account
            </Link>
          </div>
        )}
      </header>

      <main className="content-frame">
        <Outlet />
      </main>
    </div>
  );
}

function HomePage() {
  const { status } = useAuth();

  if (status === "authenticated") {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <section className="hero-grid">
      <div className="hero-copy card glass">
        <p className="eyebrow">Account system and authenticated shell</p>
        <h1>One clean place to register, sign in, and land on a personal dashboard.</h1>
        <p className="lede">
          This milestone keeps the scope narrow: account creation, password auth,
          session persistence, and a dashboard shell ready for shared lists.
        </p>
        <div className="cta-row">
          <Link to="/signup" className="primary-button">
            Create account
          </Link>
          <Link to="/login" className="secondary-button">
            Sign in
          </Link>
        </div>
      </div>

      <div className="stack-panel">
        <article className="card feature-card">
          <span className="feature-label">Protected session</span>
          <strong>HttpOnly cookie with server-side session lookup.</strong>
          <p>Users stay signed in until logout or expiry.</p>
        </article>
        <article className="card feature-card">
          <span className="feature-label">Personal dashboard</span>
          <strong>Owned lists now, shared-with-me coming online next.</strong>
          <p>The shell is ready for milestone 3 wishlist flows.</p>
        </article>
      </div>
    </section>
  );
}

function SignUpPage() {
  const { status, refreshAuth } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [values, setValues] = useState<SignUpValues>({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
  });
  const [errors, setErrors] = useState<SignUpErrors>({});
  const [formError, setFormError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  if (status === "authenticated") {
    return <Navigate to="/dashboard" replace />;
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const nextErrors = validateSignUpForm(values);
    setErrors(nextErrors);
    setFormError("");

    if (Object.keys(nextErrors).length > 0) {
      return;
    }

    setSubmitting(true);
    try {
      await signUp(normalizeSignUpValues(values));
      await refreshAuth();
      navigate(resolvePostAuthDestination(`?${searchParams.toString()}`));
    } catch (error) {
      setFormError(resolveApiErrorMessage(error, "Unable to create account"));
      setErrors(resolveApiFieldErrors(error));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <AuthCard
      title="Create your account"
      description="Register with your name, email, and password to unlock the dashboard shell."
      footer={
        <>
          Already have an account? <Link to="/login">Sign in</Link>
        </>
      }
    >
      <form className="auth-form" action={resolveApiUrl("/api/v1/auth/register")} method="post" onSubmit={handleSubmit} noValidate>
        <Field label="First name" error={errors.firstName}>
          <input
            name="firstName"
            value={values.firstName}
            onChange={(event) => setValues({ ...values, firstName: event.target.value })}
            autoComplete="given-name"
          />
        </Field>
        <Field label="Last name" error={errors.lastName}>
          <input
            name="lastName"
            value={values.lastName}
            onChange={(event) => setValues({ ...values, lastName: event.target.value })}
            autoComplete="family-name"
          />
        </Field>
        <Field label="Email" error={errors.email}>
          <input
            name="email"
            value={values.email}
            onChange={(event) => setValues({ ...values, email: event.target.value })}
            autoComplete="email"
            inputMode="email"
          />
        </Field>
        <Field label="Password" error={errors.password} hint="Use at least 8 characters.">
          <input
            type="password"
            name="password"
            value={values.password}
            onChange={(event) => setValues({ ...values, password: event.target.value })}
            autoComplete="new-password"
          />
        </Field>
        {formError ? <p className="form-error" role="alert">{formError}</p> : null}
        <button className="primary-button submit-button" type="submit" disabled={submitting}>
          {submitting ? "Creating account..." : "Create account"}
        </button>
      </form>
    </AuthCard>
  );
}

function SignInPage() {
  const { status, refreshAuth } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [values, setValues] = useState<SignInValues>({ email: "", password: "" });
  const [errors, setErrors] = useState<SignInErrors>({});
  const [formError, setFormError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  if (status === "authenticated") {
    return <Navigate to="/dashboard" replace />;
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const nextErrors = validateSignInForm(values);
    setErrors(nextErrors);
    setFormError("");

    if (Object.keys(nextErrors).length > 0) {
      return;
    }

    setSubmitting(true);
    try {
      await signIn(normalizeSignInValues(values));
      await refreshAuth();
      navigate(resolvePostAuthDestination(`?${searchParams.toString()}`));
    } catch (error) {
      setFormError(resolveApiErrorMessage(error, "Invalid email or password"));
      setErrors(resolveApiFieldErrors(error));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <AuthCard
      title="Sign in"
      description="Use the same email and password you created at sign-up."
      footer={
        <>
          Need an account? <Link to="/signup">Create one</Link>
        </>
      }
    >
      <form className="auth-form" action={resolveApiUrl("/api/v1/auth/login")} method="post" onSubmit={handleSubmit} noValidate>
        <Field label="Email" error={errors.email}>
          <input
            name="email"
            value={values.email}
            onChange={(event) => setValues({ ...values, email: event.target.value })}
            autoComplete="email"
            inputMode="email"
          />
        </Field>
        <Field label="Password" error={errors.password}>
          <input
            type="password"
            name="password"
            value={values.password}
            onChange={(event) => setValues({ ...values, password: event.target.value })}
            autoComplete="current-password"
          />
        </Field>
        {formError ? <p className="form-error" role="alert">{formError}</p> : null}
        <button className="primary-button submit-button" type="submit" disabled={submitting}>
          {submitting ? "Signing in..." : "Sign in"}
        </button>
      </form>
    </AuthCard>
  );
}

function DashboardPage() {
  const { user } = useAuth();
  const [dashboard, setDashboard] = useState<DashboardSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [form, setForm] = useState({ name: "", description: "", visibility: "private" as const });
  const [submitting, setSubmitting] = useState(false);
  const [info, setInfo] = useState("");
  const [selectedWishlistId, setSelectedWishlistId] = useState<string | null>(null);
  const [shareLinks, setShareLinks] = useState<ShareLink[]>([]);
  const [shareLabel, setShareLabel] = useState("");
  const [sharePasscode, setSharePasscode] = useState("");
  const [memberUserId, setMemberUserId] = useState("");
  const [memberRole, setMemberRole] = useState<"collaborator" | "viewer">("viewer");

  const selectedWishlist =
    dashboard?.ownedLists.find((item) => item.id === selectedWishlistId) ?? dashboard?.ownedLists[0] ?? null;

  const refreshDashboard = async () => {
    const response = await loadDashboard();
    setDashboard(response.dashboard);
    const firstOwned = response.dashboard.ownedLists[0];
    if (!selectedWishlistId && firstOwned) {
      setSelectedWishlistId(firstOwned.id);
    }
  };

  useEffect(() => {
    let mounted = true;

    async function load() {
      try {
        const response = await loadDashboard();
        if (!mounted) return;
        setDashboard(response.dashboard);
        const firstOwned = response.dashboard.ownedLists[0];
        if (firstOwned && !selectedWishlistId) {
          setSelectedWishlistId(firstOwned.id);
        }
      } catch (requestError) {
        if (!mounted) return;
        setError(resolveApiErrorMessage(requestError, "Unable to load dashboard"));
      } finally {
        if (mounted) setLoading(false);
      }
    }

    void load();

    return () => {
      mounted = false;
    };
  }, [selectedWishlistId]);

  useEffect(() => {
    if (!selectedWishlist) {
      setShareLinks([]);
      return;
    }

    const selectedId = selectedWishlist.id;

    async function loadLinks() {
      try {
        const response = await listWishlistShareLinks(selectedId);
        setShareLinks(response.links);
      } catch (requestError) {
        setError(resolveApiErrorMessage(requestError, "Unable to load share links"));
      }
    }

    void loadLinks();
  }, [selectedWishlist]);

  async function handleCreate(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError("");
    setInfo("");
    if (!form.name.trim()) {
      setError("Wishlist name is required");
      return;
    }

    setSubmitting(true);
    try {
      await createWishlist({
        name: form.name,
        description: form.description || null,
        visibility: form.visibility,
      });
      setForm({ name: "", description: "", visibility: "private" });
      await refreshDashboard();
      setInfo("Wishlist created.");
    } catch (requestError) {
      setError(resolveApiErrorMessage(requestError, "Unable to create wishlist"));
    } finally {
      setSubmitting(false);
    }
  }

  const runDelete = async (wishlistId: string) => {
    const confirmed = window.confirm("Delete this wishlist? This action cannot be undone.");
    if (!confirmed) return;

    setError("");
    try {
      await deleteWishlist(wishlistId);
      await refreshDashboard();
      setInfo("Wishlist deleted.");
    } catch (requestError) {
      setError(resolveApiErrorMessage(requestError, "Unable to delete wishlist"));
    }
  };

  const runClone = async (wishlistId: string) => {
    setError("");
    try {
      const result = await cloneWishlist(wishlistId);
      await refreshDashboard();
      setInfo(`Cloned to “${result.wishlist.name}”.`);
    } catch (requestError) {
      setError(resolveApiErrorMessage(requestError, "Unable to clone wishlist"));
    }
  };

  const saveVisibility = async () => {
    if (!selectedWishlist) {
      return;
    }

    setError("");
    try {
      await updateWishlist(selectedWishlist.id, {
        visibility: selectedWishlist.visibility,
        version: selectedWishlist.version,
      });
      await refreshDashboard();
      setInfo("Visibility updated.");
    } catch (requestError) {
      setError(resolveApiErrorMessage(requestError, "Unable to update visibility"));
    }
  };

  const savePasscode = async () => {
    if (!selectedWishlist || !sharePasscode.trim()) {
      setError("Enter a passcode to save.");
      return;
    }

    setError("");
    try {
      await setWishlistPasscode(selectedWishlist.id, { code: sharePasscode.trim() });
      setSharePasscode("");
      setInfo("Passcode updated.");
    } catch (requestError) {
      setError(resolveApiErrorMessage(requestError, "Unable to update passcode"));
    }
  };

  const createShareLink = async () => {
    if (!selectedWishlist) {
      return;
    }

    setError("");
    try {
      const result = await createWishlistShareLink(selectedWishlist.id, { label: shareLabel.trim() || null });
      await listWishlistShareLinks(selectedWishlist.id).then((response) => setShareLinks(response.links));
      setShareLabel("");
      setInfo(`Share link created: ${result.link.token}`);
    } catch (requestError) {
      setError(resolveApiErrorMessage(requestError, "Unable to create share link"));
    }
  };

  const revokeShareLink = async (linkId: string) => {
    if (!selectedWishlist) {
      return;
    }

    setError("");
    try {
      await revokeWishlistShareLink(selectedWishlist.id, linkId);
      const response = await listWishlistShareLinks(selectedWishlist.id);
      setShareLinks(response.links);
      setInfo("Share link revoked.");
    } catch (requestError) {
      setError(resolveApiErrorMessage(requestError, "Unable to revoke share link"));
    }
  };

  const addMember = async () => {
    if (!selectedWishlist || !memberUserId.trim()) {
      setError("Select a user ID to invite.");
      return;
    }

    setError("");
    try {
      await addWishlistMember(selectedWishlist.id, { userId: memberUserId.trim(), role: memberRole });
      setMemberUserId("");
      setInfo("Member added.");
    } catch (requestError) {
      setError(resolveApiErrorMessage(requestError, "Unable to add member"));
    }
  };

  return (
    <section className="dashboard-grid">
      <div className="dashboard-hero card glass">
        <p className="eyebrow">Authenticated dashboard</p>
        <h1>Welcome back, {user?.firstName}.</h1>
        <p className="lede">Create your own wishlists, set visibility, and clone your current lists when you want a fresh start.</p>
      </div>

      <form className="card dashboard-form" onSubmit={handleCreate}>
        <div className="section-heading">
          <div>
            <p className="eyebrow">Create a wishlist</p>
            <h2>New list</h2>
          </div>
        </div>
        <label className="field compact">
          <span>List name</span>
          <input value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })} placeholder="Wedding registry" />
        </label>
        <label className="field compact">
          <span>Description</span>
          <textarea value={form.description} onChange={(event) => setForm({ ...form, description: event.target.value })} placeholder="Optional notes" rows={3} />
        </label>
        <label className="field compact">
          <span>Visibility</span>
          <select value={form.visibility} onChange={(event) => setForm({ ...form, visibility: event.target.value as typeof form.visibility })}>
            <option value="private">Private</option>
            <option value="public">Public</option>
            <option value="protected">Protected</option>
            <option value="limited">Limited</option>
          </select>
        </label>
        <button className="primary-button" type="submit" disabled={submitting}>
          {submitting ? "Creating..." : "Create wishlist"}
        </button>
      </form>

      {error ? <div className="card alert-card" role="alert">{error}</div> : null}
      {info ? <div className="card success-card" role="status">{info}</div> : null}

      <div className="dashboard-columns">
        <DashboardSection
          title="Owned lists"
          description="Lists you create will surface here."
          items={dashboard?.ownedLists ?? []}
          loading={loading}
          emptyLabel="No owned lists yet. Create your first list above."
          selectedWishlistId={selectedWishlistId}
          onSelect={setSelectedWishlistId}
          onDelete={runDelete}
          onClone={runClone}
        />
        <DashboardSection
          title="Shared with me"
          description="Invitations and direct shares will collect here."
          items={dashboard?.sharedWithMe ?? []}
          loading={loading}
          emptyLabel="Shared-with-me is ready as a placeholder for now."
          placeholder
        />
      </div>

      {selectedWishlist ? (
        <section className="card dashboard-manager">
          <div className="section-heading">
            <div>
              <p className="eyebrow">Manage selected list</p>
              <h2>{selectedWishlist.name}</h2>
            </div>
          </div>

          <div className="manager-grid">
            <div className="manager-panel">
              <h3>Visibility</h3>
              <label className="field compact">
                <span>Mode</span>
                <select
                  value={selectedWishlist.visibility}
                  onChange={(event) => {
                    const nextVisibility = event.target.value as DashboardSummary["ownedLists"][number]["visibility"];
                    setDashboard((current) => {
                      if (!current) return current;
                      return {
                        ...current,
                        ownedLists: current.ownedLists.map((wishlist) =>
                          wishlist.id === selectedWishlist.id ? { ...wishlist, visibility: nextVisibility } : wishlist
                        ),
                      };
                    });
                  }}
                >
                  <option value="private">Private</option>
                  <option value="public">Public</option>
                  <option value="protected">Protected</option>
                  <option value="limited">Limited</option>
                </select>
              </label>
              <button type="button" className="primary-button" onClick={saveVisibility}>Save visibility</button>
            </div>

            <div className="manager-panel">
              <h3>Passcode</h3>
              <label className="field compact">
                <span>New passcode</span>
                <input
                  type="text"
                  value={sharePasscode}
                  onChange={(event) => setSharePasscode(event.target.value)}
                  placeholder="Enter a passcode"
                />
              </label>
              <button type="button" className="secondary-button" onClick={savePasscode}>Set passcode</button>
            </div>

            <div className="manager-panel span-two">
              <h3>Share links</h3>
              <div className="inline-form">
                <input
                  type="text"
                  value={shareLabel}
                  onChange={(event) => setShareLabel(event.target.value)}
                  placeholder="Optional label"
                />
                <button type="button" className="primary-button" onClick={createShareLink}>Create link</button>
              </div>
              {shareLinks.length > 0 ? (
                <ul className="share-link-list">
                  {shareLinks.map((link) => (
                    <li className="share-link-item" key={link.id}>
                      <div>
                        <strong>{link.label ?? "Share link"}</strong>
                        <code>{link.token}</code>
                      </div>
                      <button type="button" className="ghost-button" onClick={() => void navigator.clipboard.writeText(link.token)}>
                        Copy
                      </button>
                      <button type="button" className="ghost-button danger-button" onClick={() => void revokeShareLink(link.id)}>
                        Revoke
                      </button>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="muted-copy">No active share links yet.</p>
              )}
            </div>

            <div className="manager-panel span-two">
              <h3>Members</h3>
              <div className="inline-form">
                <input
                  type="text"
                  value={memberUserId}
                  onChange={(event) => setMemberUserId(event.target.value)}
                  placeholder="User ID"
                />
                <select value={memberRole} onChange={(event) => setMemberRole(event.target.value as "collaborator" | "viewer")}>
                  <option value="viewer">Viewer</option>
                  <option value="collaborator">Collaborator</option>
                </select>
                <button type="button" className="secondary-button" onClick={addMember}>Add member</button>
              </div>
            </div>
          </div>
          <ItemManager wishlistId={selectedWishlist.id} wishlistName={selectedWishlist.name} />
        </section>
      ) : null}
    </section>
  );
}

function DashboardSection({
  title,
  description,
  items,
  loading,
  emptyLabel,
  placeholder = false,
  selectedWishlistId,
  onSelect,
  onDelete,
  onClone,
}: {
  title: string;
  description: string;
  items: DashboardSummary["ownedLists"];
  loading: boolean;
  emptyLabel: string;
  placeholder?: boolean;
  selectedWishlistId?: string | null;
  onSelect?: (wishlistId: string) => void;
  onDelete?: (wishlistId: string) => void;
  onClone?: (wishlistId: string) => void;
}) {
  return (
    <section className="card dashboard-section">
      <div className="section-heading">
        <div>
          <p className="eyebrow">{title}</p>
          <h2>{description}</h2>
        </div>
        {placeholder ? <span className="pill muted">Placeholder</span> : null}
      </div>

      {loading ? (
        <p className="muted-copy">Loading dashboard data...</p>
      ) : items.length > 0 ? (
        <div className="list-stack">
          {items.map((item) => (
            <article
              className={selectedWishlistId === item.id ? "list-card selected" : "list-card"}
              key={item.id}
              onClick={() => onSelect?.(item.id)}
            >
              <strong>{item.name}</strong>
              <p>{item.description ?? "No description yet."}</p>
              <div className="list-meta-row">
                <span className="pill">{item.visibility}</span>
                {onDelete ? (
                  <button
                    type="button"
                    className="ghost-button danger-button"
                    onClick={(event) => {
                      event.stopPropagation();
                      onDelete(item.id);
                    }}
                  >
                    Delete
                  </button>
                ) : null}
                {onClone ? (
                  <button
                    type="button"
                    className="secondary-button"
                    onClick={(event) => {
                      event.stopPropagation();
                      onClone(item.id);
                    }}
                  >
                    Clone
                  </button>
                ) : null}
              </div>
            </article>
          ))}
        </div>
      ) : (
        <p className="muted-copy">{emptyLabel}</p>
      )}
    </section>
  );
}

function RequireAuth({ children }: { children: JSX.Element }) {
  const { status } = useAuth();
  const location = useLocation();

  if (status === "loading") {
    return <div className="card glass">Loading your session...</div>;
  }

  if (status === "anonymous") {
    return <Navigate to={`/login?next=${encodeURIComponent(`${location.pathname}${location.search}`)}`} replace />;
  }

  return children;
}

function AuthCard({
  title,
  description,
  footer,
  children,
}: {
  title: string;
  description: string;
  footer: ReactNode;
  children: ReactNode;
}) {
  return (
    <section className="auth-layout">
      <div className="card auth-card glass">
        <p className="eyebrow">Account access</p>
        <h1>{title}</h1>
        <p className="lede">{description}</p>
        {children}
        <p className="auth-footer">{footer}</p>
      </div>
    </section>
  );
}

function Field({
  label,
  error,
  hint,
  children,
}: {
  label: string;
  error: string | undefined;
  hint?: string | undefined;
  children: ReactNode;
}) {
  return (
    <label className="field">
      <span>
        {label}
        {hint ? <small>{hint}</small> : null}
      </span>
      {children}
      {error ? <strong className="field-error">{error}</strong> : null}
    </label>
  );
}

function initials(firstName?: string, lastName?: string): string {
  return `${firstName?.[0] ?? ""}${lastName?.[0] ?? ""}`.trim().toUpperCase() || "?";
}

function resolveApiErrorMessage(error: unknown, fallback: string): string {
  if (error instanceof ApiError) {
    return error.message || fallback;
  }
  if (error instanceof Error && error.message.trim().length > 0) {
    return error.message;
  }
  return fallback;
}

function resolveApiFieldErrors(error: unknown): SignInErrors & SignUpErrors {
  if (error instanceof ApiError && error.fields) {
    return error.fields as SignInErrors & SignUpErrors;
  }
  return {};
}
